/**
 * MediaRecorder fallback for browsers without WebCodecs. It records
 * canvas.captureStream(fps) in REAL TIME off the live compositor, so the
 * caller must pace frames to the wall clock (`realtime: true`) and the output
 * is capped well below what WebCodecs can do.
 *
 * The recorder is created at the first frame rather than up front, because
 * a MediaRecorder's track set is fixed when it is constructed and audio is
 * attached after the sink exists.
 */

import type { EncoderPlan } from './encoderCapabilities';
import { abortError } from './exportErrors';
import type { CreateSinkOptions, EncodedVideoResult, VideoEncoderSink } from './videoEncoder';

// Keep encode modest for MediaRecorder reliability on Windows Chrome/Edge
// (createKenBurnsReel used 540; at higher sizes Chrome/Edge on Windows drop
// frames and sometimes emit a 0-byte blob. 720 is the tested ceiling.)
export const MEDIARECORDER_MAX_SIDE = 720;

/** Below this the recorder produced nothing usable (same detector as createKenBurnsReel). */
const EMPTY_BLOB_BYTES = 500;

function nextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/** With audio, ask for Opus explicitly; some browsers reject a video-only codec list plus an audio track. */
function recorderMimes(base: string, withAudio: boolean): string[] {
  if (!withAudio || base.includes('opus') || !base.startsWith('video/webm;codecs=')) return [base];
  return [`${base},opus`, base];
}

export async function createMediaRecorderSink(
  opts: CreateSinkOptions & { plan: EncoderPlan }
): Promise<VideoEncoderSink> {
  const { plan, signal } = opts;
  const { width, height, fps } = plan;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  if (typeof MediaRecorder === 'undefined' || typeof canvas.captureStream !== 'function') {
    throw new Error('This browser can’t record video. Use a recent Chrome or Edge to export.');
  }

  const stream = canvas.captureStream(fps);
  const chunks: BlobPart[] = [];
  const startHooks: Array<() => void> = [];
  let recorder: MediaRecorder | null = null;
  let stopped: Promise<void> = Promise.resolve();
  let aborted = false;
  let startedAt = 0;
  let disposed = false;

  const createRecorder = (): MediaRecorder => {
    const withAudio = stream.getAudioTracks().length > 0;
    for (const mimeType of recorderMimes(plan.videoCodecString, withAudio)) {
      if (!MediaRecorder.isTypeSupported(mimeType)) continue;
      try {
        return new MediaRecorder(stream, { mimeType, videoBitsPerSecond: plan.videoBitrate });
      } catch {
        /* try the next */
      }
    }
    try {
      return new MediaRecorder(stream);
    } catch {
      throw new Error('Could not start the video recorder in this browser.');
    }
  };

  const disposeAll = () => {
    if (disposed) return;
    disposed = true;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        /* already stopping */
      }
    }
    stream.getTracks().forEach((t) => t.stop());
  };

  return {
    backend: 'mediarecorder',
    canvas,
    realtime: true,

    async addFrame(index) {
      if (signal?.aborted) throw abortError();
      if (index === 0) {
        const rec = createRecorder();
        recorder = rec;
        rec.ondataavailable = (e) => {
          if (!aborted && e.data && e.data.size > 0) chunks.push(e.data);
        };
        stopped = new Promise<void>((resolve, reject) => {
          rec.onstop = () => resolve();
          rec.onerror = () => reject(new Error('Video recording failed.'));
        });
        // Abort can leave this rejected with nobody awaiting it.
        stopped.catch(() => undefined);
        // Start only once frame 0 is painted so the recording doesn't open on a blank canvas.
        rec.start(200);
        startHooks.forEach((fn) => fn());
        await nextAnimationFrame();
        startedAt = performance.now();
      }
      if (!recorder || recorder.state === 'inactive') {
        throw new Error('The video recorder stopped unexpectedly.');
      }
      // Hold this frame on screen until its slot ends; the track samples on its own clock.
      const due = startedAt + ((index + 1) * 1000) / fps;
      while (performance.now() < due) {
        if (signal?.aborted) throw abortError();
        await nextAnimationFrame();
      }
    },

    attachAudioTrack(track, onStart) {
      if (recorder) throw new Error('Audio must be attached before recording starts.');
      stream.addTrack(track);
      if (onStart) startHooks.push(onStart);
    },

    async finalize(): Promise<EncodedVideoResult> {
      try {
        const rec = recorder;
        if (!rec) throw new Error('The recording never started.');
        if (rec.state === 'recording') {
          try {
            rec.requestData();
          } catch {
            /* optional */
          }
          rec.stop();
        }
        await stopped;
        const mimeType = rec.mimeType || plan.videoCodecString || 'video/webm';
        const blob = new Blob(chunks, { type: mimeType });
        if (blob.size < EMPTY_BLOB_BYTES) {
          throw new Error('The recording came out empty. Try Chrome or Edge, or a shorter video.');
        }
        return {
          blob,
          mimeType,
          width,
          height,
          fps,
          durationMs: opts.durationMs,
          backend: 'mediarecorder',
          note: plan.reason,
        };
      } finally {
        disposeAll();
      }
    },

    async abort() {
      aborted = true;
      chunks.length = 0;
      disposeAll();
    },
  };
}
